import { useCallback, useEffect, useState } from "react";
import { friendlyError, partnerApi, type PartnerBot } from "@/lib/api";
import { Button, Card, ErrorText, Field, PageLoader, Badge } from "@/components/ui";

export function Bots() {
  const [bots, setBots] = useState<PartnerBot[] | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    partnerApi.bots().then((r) => setBots(r.bots)).catch((e) => setErr(friendlyError(e)));
  }, []);
  useEffect(load, [load]);

  if (err && bots === null) return <Card className="mt-4"><ErrorText>{err}</ErrorText></Card>;
  if (bots === null) return <PageLoader />;

  return (
    <div className="space-y-3 pt-2">
      <div>
        <div className="text-xl font-bold">Мои боты</div>
        <p className="mt-1 text-sm text-white/45">
          Подключите свой Telegram-бот — он выглядит как наш, но с вашим кодом. Клиенты приходят
          в него, а вознаграждение идёт вам.
        </p>
      </div>

      <AddBot onAdded={load} />

      {bots.length === 0 && (
        <Card><p className="text-sm text-white/50">Пока нет подключённых ботов.</p></Card>
      )}

      {bots.map((b) => (
        <BotRow key={b.id} bot={b} onChanged={load} />
      ))}
    </div>
  );
}

function AddBot({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!token.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await partnerApi.addBot(token.trim(), title.trim() || undefined);
      setToken("");
      setTitle("");
      setOpen(false);
      onAdded();
    } catch (e) {
      setError(friendlyError(e, "Не удалось подключить бота."));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button className="w-full" onClick={() => setOpen(true)}>
        + Подключить бота
      </Button>
    );
  }
  return (
    <Card className="space-y-3">
      <div className="text-sm font-medium text-white/70">Новый бот</div>
      <p className="text-xs text-white/45">
        Создайте бота у <span className="font-mono">@BotFather</span> и вставьте его токен
        (вида <span className="font-mono">123456:AA…</span>). Вебхук и кнопку настроим сами.
      </p>
      <Field label="Токен бота" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456:AA…" />
      <Field label="Название (необязательно)" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Напр. «Основной канал»" />
      <ErrorText>{error}</ErrorText>
      <div className="flex gap-2">
        <Button className="flex-1" loading={busy} disabled={!token.trim()} onClick={submit}>
          Подключить
        </Button>
        <Button variant="ghost" onClick={() => { setOpen(false); setError(""); }}>
          Отмена
        </Button>
      </div>
    </Card>
  );
}

function BotRow({ bot, onChanged }: { bot: PartnerBot; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [markup, setMarkup] = useState(bot.markup_pct != null ? String(bot.markup_pct) : "");
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [markupErr, setMarkupErr] = useState("");

  const saveMarkup = async () => {
    const v = Number(markup.replace(",", "."));
    if (!Number.isFinite(v) || v < 0) {
      setMarkupErr("Введите неотрицательное число");
      return;
    }
    setSavingMarkup(true);
    setMarkupErr("");
    try {
      await partnerApi.setBotMarkup(bot.id, v);
      onChanged();
    } catch (e) {
      setMarkupErr(friendlyError(e, "Не удалось сохранить ставку."));
    } finally {
      setSavingMarkup(false);
    }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      await partnerApi.toggleBot(bot.id, !bot.enabled);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!confirm(`Удалить бота @${bot.username ?? ""}? Клиенты останутся закреплены за вами.`)) return;
    setBusy(true);
    try {
      await partnerApi.deleteBot(bot.id);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    if (!bot.link) return;
    try {
      await navigator.clipboard.writeText(bot.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Card className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">
            {bot.title || (bot.username ? `@${bot.username}` : `bot ${bot.tg_bot_id}`)}
          </div>
          {bot.username && bot.title && <div className="text-xs text-white/40">@{bot.username}</div>}
        </div>
        <Badge tone={bot.enabled ? "green" : "muted"}>{bot.enabled ? "Активен" : "Выключен"}</Badge>
      </div>

      {bot.code && (
        <div className="text-xs text-white/45">
          Код: <span className="font-mono text-white/80">{bot.code.toUpperCase()}</span>
        </div>
      )}

      {bot.link && (
        <button type="button" onClick={copy} className="block w-full text-left">
          <div className="rounded-xl bg-surface-raised px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm text-white/80">{bot.link}</span>
              <span className={`shrink-0 text-xs ${copied ? "text-brand" : "text-white/40"}`}>
                {copied ? "Скопировано" : "Копировать"}
              </span>
            </div>
          </div>
        </button>
      )}

      <div className="rounded-xl bg-surface-raised px-3 py-2 space-y-1.5">
        <div className="text-xs text-white/45">
          Ставка бота (надбавка к курсу), %{!bot.markup_overridden && " · сейчас общая партнёрская"}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field
              value={markup}
              inputMode="decimal"
              placeholder="напр. 3"
              onChange={(e) => setMarkup(e.target.value)}
            />
          </div>
          <Button loading={savingMarkup} onClick={saveMarkup}>
            Задать
          </Button>
        </div>
        {markupErr && <ErrorText>{markupErr}</ErrorText>}
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="ghost" className="flex-1" loading={busy} onClick={toggle}>
          {bot.enabled ? "Выключить" : "Включить"}
        </Button>
        <Button variant="danger" disabled={busy} onClick={remove}>
          Удалить
        </Button>
      </div>
    </Card>
  );
}
